import type {
  SingleEntityDefinition,
  ActionDefinition,
  EventEntityDefinition,
  ComputedEntityDefinition,
  ModuleContext
} from '@gzl10/nexus-sdk'
import { useIdField, useTextField, useSelectField, useNumberField, useSwitchField, useEmailField, usePasswordField, useTextareaField, useTagsField, useDatetimeField } from '@gzl10/nexus-sdk/fields'
import nodemailer from 'nodemailer'
import { getMailService } from './mail.service.js'
import type { SendMailInput, SendMailOptions, MailConfigRow } from './mail.types.js'

/**
 * Get mail config via SingleService (single_records KV).
 * With envMapping, env vars are already resolved by SingleService.findById/get.
 */
async function getMailConfigFromDB(ctx: ModuleContext): Promise<MailConfigRow> {
  const configService = ctx.services['mail_config'] as import('@gzl10/nexus-sdk').BaseEntityService | undefined
  if (configService && 'get' in configService) {
    const row = await (configService as { get: () => Promise<Record<string, unknown> | null> }).get()
    if (row) {
      return {
        host: (row['host'] as string) || 'localhost',
        port: (row['port'] as number) || 587,
        secure: row['secure'] === true || row['secure'] === 1,
        from: (row['from'] as string) || 'noreply@localhost',
        auth_user: (row['auth_user'] as string) ?? null,
        auth_pass: (row['auth_pass'] as string) ?? null
      }
    }
  }

  // Fallback: defaults only (no more mailEnv import)
  return {
    host: 'localhost',
    port: 587,
    secure: false,
    from: 'noreply@localhost',
    auth_user: null,
    auth_pass: null
  }
}

/**
 * Create nodemailer transporter from config
 */
function createTransporter(config: MailConfigRow) {
  const auth = config.auth_user
    ? { user: config.auth_user, pass: config.auth_pass! }
    : undefined

  return nodemailer.createTransport({
    host: config.host,
    port: config.port,
    secure: config.secure as boolean,
    auth,
    ...(auth ? {} : { ignoreTLS: true })
  })
}

/** Normalize to to array */
function normalizeRecipients(to: string | string[]): string[] {
  if (Array.isArray(to)) return to
  return to.split(',').map(s => s.trim()).filter(Boolean)
}

/**
 * Config Entity: config
 * SMTP configuration persisted in the DB
 */
export const mailConfigEntity: SingleEntityDefinition = {
  type: 'config',
  key: 'mail_config',
  label: { en: 'Config', es: 'Configuración' },
  routePrefix: '/config',
  timestamps: true,
  order: 2,

  defaults: {
    host: 'localhost',
    port: 587,
    secure: false,
    from: 'noreply@localhost',
    auth_user: '',
    auth_pass: ''
  },

  envMapping: {
    host: { env: 'SMTP_HOST' },
    port: { env: 'SMTP_PORT', parse: 'number' },
    secure: { env: 'SMTP_SECURE', parse: 'boolean' },
    from: { env: 'SMTP_FROM' },
    auth_user: { env: 'SMTP_USER' },
    auth_pass: { env: 'SMTP_PASS' }
  },

  fields: {
    id: useIdField(),
    host: useTextField({
      label: { en: 'SMTP Host', es: 'Host SMTP' },
      size: 255,
      nullable: false,
      hint: { en: 'Default: SMTP_HOST env var', es: 'Por defecto: variable SMTP_HOST' }
    }),
    port: useNumberField({
      label: { en: 'Port', es: 'Puerto' },
      nullable: false,
      hint: { en: 'Default: SMTP_PORT env var', es: 'Por defecto: variable SMTP_PORT' }
    }),
    secure: useSwitchField({
      label: { en: 'TLS/SSL', es: 'TLS/SSL' },
      hint: { en: 'Default: SMTP_SECURE env var', es: 'Por defecto: variable SMTP_SECURE' }
    }),
    from: useEmailField({
      label: { en: 'Sender', es: 'Remitente' },
      nullable: false,
      hint: { en: 'Default: SMTP_FROM env var', es: 'Por defecto: variable SMTP_FROM' }
    }),
    auth_user: useTextField({
      label: { en: 'Auth User', es: 'Usuario de autenticación' },
      size: 255,
      nullable: true,
      hint: { en: 'SMTP username (optional)', es: 'Usuario SMTP (opcional)' }
    }),
    auth_pass: usePasswordField({
      label: { en: 'Auth Password', es: 'Contraseña de autenticación' },
      size: 255,
      nullable: true,
      hint: { en: 'SMTP password (optional)', es: 'Contraseña SMTP (opcional)' }
    })
  },

  casl: {
    subject: 'MailConfig',
    sensitiveFields: ['auth_pass'],
    permissions: {
      ADMIN: { actions: ['read', 'update'] },
      SUPPORT: { actions: ['read'] }
    }
  }
}

/**
 * Action Entity: send
 * Endpoint for sending emails
 */
export const mailSendAction: ActionDefinition = {
  key: 'send',
  scope: 'module',
  label: { en: 'Send Email', es: 'Enviar correo' },
  variant: 'primary',
  output: {
    message: useTextField({ label: { en: 'Result', es: 'Resultado' } }),
    messageId: useTextField({ label: { en: 'Message ID', es: 'ID de mensaje' } })
  },

  input: {
    to: useTagsField({
      label: { en: 'Recipient(s)', es: 'Destinatario(s)' },
      placeholder: { en: 'Add email address', es: 'Añadir dirección de correo' },
      required: true,
      validation: { format: 'email' }
    }),
    subject: useTextField({
      label: { en: 'Subject', es: 'Asunto' },
      required: true,
      validation: { min: 1, max: 255 }
    }),
    title: useTextField({
      label: { en: 'Title', es: 'Título' },
      hint: { en: 'Large title in email header', es: 'Título grande en la cabecera del correo' }
    }),
    message: {
      label: { en: 'Message', es: 'Mensaje' },
      input: 'markdown',
      hint: { en: 'Email body (supports markdown)', es: 'Cuerpo del correo (soporta markdown)' }
    },
    html: useTextareaField({
      label: { en: 'HTML', es: 'HTML' },
      hidden: true,
      hint: { en: 'Alternative HTML (ignores title/message if provided)', es: 'HTML alternativo (ignora título/mensaje si se proporciona)' }
    })
  },

  handler: async (ctx: ModuleContext, input: unknown) => {
    const { to: rawTo, subject, title, message, html, _authUserId } = input as SendMailInput & { _authUserId?: string }
    if (!rawTo) {
      throw new ctx.core.errors.ValidationError('Recipient (to) is required')
    }
    const recipients = normalizeRecipients(rawTo)

    // Get mail config from DB
    const config = await getMailConfigFromDB(ctx)
    const transporter = createTransporter(config)

    // Use MailService for template rendering if available
    const mailService = getMailService()

    // Build email options
    const mailOptions: SendMailOptions = {
      to: recipients,
      subject,
      title,
      message,
      html,
      from: config.from
    }

    // Send using dynamic transporter but delegate to service for template
    let result: { messageId: string; accepted: string[]; rejected: string[] } | null = null
    try {
      // If using title/message, let service render template
      if (title || message) {
        result = await mailService.send(mailOptions)
      } else {
        // Direct send with raw html
        const sendResult = await transporter.sendMail({
          from: config.from,
          to: recipients.join(', '),
          subject,
          html
        })
        result = {
          messageId: sendResult.messageId,
          accepted: sendResult.accepted as string[],
          rejected: sendResult.rejected as string[]
        }
      }
    } catch (error) {
      ctx.core.logger.warn({ error }, 'Failed to send email')

      // Log del fallo
      await ctx.db.knex('mail_log').insert({
        id: ctx.core.generateId(),
        to: recipients.join(', '),
        subject,
        status: 'failed',
        message_id: null,
        error: error instanceof Error ? error.message : 'Failed to send email',
        sent_by: _authUserId ?? null,
        created_at: ctx.db.nowTimestamp(ctx.db.knex)
      })

      throw new ctx.core.errors.AppError('Failed to send email', 500)
    }

    // Guard: result siempre definido si llegamos aquí
    if (!result) {
      throw new ctx.core.errors.AppError('Failed to send email', 500)
    }

    // Log del éxito
    await ctx.db.knex('mail_log').insert({
      id: ctx.core.generateId(),
      to: recipients.join(', '),
      subject,
      status: 'sent',
      message_id: result.messageId,
      error: null,
      sent_by: _authUserId ?? null,
      created_at: ctx.db.nowTimestamp(ctx.db.knex)
    })

    const acceptedCount = result.accepted.length
    const rejectedCount = result.rejected.length
    let resultMessage = `Email sent to ${acceptedCount} recipient${acceptedCount !== 1 ? 's' : ''}`
    if (rejectedCount > 0) {
      resultMessage += ` (${rejectedCount} rejected)`
    }

    return {
      message: resultMessage,
      messageId: result.messageId,
      accepted: result.accepted,
      rejected: result.rejected
    }
  },

  casl: {
    subject: 'MailSend',
    permissions: {
      ADMIN: { actions: ['execute'] }
    }
  }
}

/**
 * Action Entity: test
 * Probar conexión SMTP
 */
export const mailTestAction: ActionDefinition = {
  key: 'test',
  scope: 'module',
  label: { en: 'Test Connection', es: 'Probar conexión' },
  order: 1,
  output: {
    host: useTextField({ label: { en: 'Host', es: 'Host' } }),
    port: useNumberField({ label: { en: 'Port', es: 'Puerto' } }),
    message: useTextField({ label: { en: 'Result', es: 'Resultado' } })
  },

  handler: async (ctx: ModuleContext) => {
    // Get mail config from DB
    const config = await getMailConfigFromDB(ctx)
    const transporter = createTransporter(config)

    try {
      await transporter.verify()
      ctx.core.logger.info({ host: config.host, port: config.port }, 'SMTP connection verified')
    } catch (error) {
      ctx.core.logger.warn({ error }, 'SMTP connection failed')
      throw new ctx.core.errors.AppError(
        `Failed to connect to SMTP server ${config.host}:${config.port}`,
        500
      )
    }

    return {
      host: config.host,
      port: config.port,
      message: `SMTP connection to ${config.host}:${config.port} verified successfully`
    }
  },

  casl: {
    subject: 'MailTest',
    permissions: {
      ADMIN: { actions: ['execute'] }
    }
  }
}

/**
 * Event Entity: log
 * Append-only log of sent emails
 */
export const mailLogEntity: EventEntityDefinition = {
  type: 'event',
  immutable: true,
  table: 'mail_log',
  label: { en: 'Log', es: 'Log' },
  labelPlural: { en: 'Mail Logs', es: 'Registros de correos' },
  labelField: 'subject',
  routePrefix: '/log',
  // timestamps: false - created_at está definido explícitamente en fields
  retention: { days: 90 },
  calendarFrom: 'created_at',
  defaultSort: { field: 'created_at', order: 'desc' },
  availableDisplayModes: ['table', 'list', 'calendar'],
  groupableFields: ['status', 'sent_by'],
  order: 1,

  fields: {
    id: useIdField(),
    created_at: useDatetimeField({
      label: { en: 'Date', es: 'Fecha' },
      disabled: true,
      nullable: false,
      meta: { sortable: true }
    }),
    to: useTextField({
      label: { en: 'Recipient(s)', es: 'Destinatario(s)' },
      size: 1000,
      nullable: false,
      meta: { searchable: true }
    }),
    subject: useTextField({
      label: { en: 'Subject', es: 'Asunto' },
      size: 255,
      nullable: false,
      meta: { searchable: true, sortable: true }
    }),
    status: {
      ...useSelectField({
        label: { en: 'Status', es: 'Estado' },
        options: [
          { value: 'pending', label: { en: 'Pending', es: 'Pendiente' } },
          { value: 'sent', label: { en: 'Sent', es: 'Enviado' } },
          { value: 'failed', label: { en: 'Failed', es: 'Fallido' } },
          { value: 'bounced', label: { en: 'Bounced', es: 'Rebotado' } }
        ],
        nullable: false,
        index: true,
        meta: { sortable: true, searchable: true }
      }),
      validation: { enum: ['pending', 'sent', 'failed', 'bounced'] }
    },
    message_id: useTextField({
      label: { en: 'Message ID', es: 'ID de mensaje' },
      hidden: true,
      size: 255,
      nullable: true
    }),
    error: useTextareaField({
      label: { en: 'Error', es: 'Error' },
      nullable: true,
      meta: { showInDisplay: false }
    }),
    sent_by: useSelectField({
      label: { en: 'Sent by', es: 'Enviado por' },
      table: 'users',
      column: 'id',
      onDelete: 'SET NULL',
      endpoint: '/users',
      valueField: 'id',
      labelField: 'name',
      nullable: true,
      index: true
    })
  },

  casl: {
    subject: 'MailLog',
    permissions: {
      SUPPORT: { actions: ['read'] }
    }
  }
}

/**
 * Overview Entity (computed, single)
 * Mail statistics dashboard
 */
export const mailOverviewEntity: ComputedEntityDefinition = {
  type: 'computed',
  label: { en: 'Overview', es: 'Resumen' },
  icon: 'mdi:chart-box-outline',
  labelField: 'total',
  isSingle: true,
  routePrefix: '/overview',
  order: 0,

  fields: {
    total: useNumberField({ label: { en: 'Total Emails', es: 'Total correos' } }),
    sent: useNumberField({ label: { en: 'Sent', es: 'Enviados' } }),
    failed: useNumberField({ label: { en: 'Failed', es: 'Fallidos' } }),
    bounced: useNumberField({ label: { en: 'Bounced', es: 'Rebotados' } }),
    sent_24h: useNumberField({ label: { en: 'Sent (24h)', es: 'Enviados (24h)' } }),
    failed_24h: useNumberField({ label: { en: 'Failed (24h)', es: 'Fallidos (24h)' } }),
    success_rate: useNumberField({ label: { en: 'Success Rate %', es: 'Tasa éxito %' } }),
    last_sent_at: useTextField({ label: { en: 'Last Sent', es: 'Último envío' } })
  },

  compute: async (ctx: ModuleContext) => {
    const { knex } = ctx.db
    const now = new Date()
    const h24ago = ctx.db.formatTimestamp(knex, new Date(now.getTime() - 24 * 60 * 60 * 1000))

    // All-time status counts
    const statusCounts = await knex('mail_log')
      .select('status')
      .count('* as count')
      .groupBy('status') as { status: string; count: string | number }[]
    const byStatus = Object.fromEntries(statusCounts.map(r => [r.status, Number(r.count)]))
    const total = statusCounts.reduce((sum, r) => sum + Number(r.count), 0)

    // Last 24h
    const recent = await knex('mail_log')
      .where('created_at', '>=', h24ago)
      .select(
        knex.raw('COUNT(*) as total'),
        knex.raw("SUM(CASE WHEN status = 'sent' THEN 1 ELSE 0 END) as sent"),
        knex.raw("SUM(CASE WHEN status = 'failed' THEN 1 ELSE 0 END) as failed")
      )
      .first()

    const totalAll = (byStatus['sent'] ?? 0) + (byStatus['failed'] ?? 0) + (byStatus['bounced'] ?? 0)
    const successRate = totalAll > 0 ? Math.round(((byStatus['sent'] ?? 0) / totalAll) * 100) : 100

    // Last sent email
    const lastSent = await knex('mail_log')
      .where('status', 'sent')
      .orderBy('created_at', 'desc')
      .select('created_at')
      .first()

    return [{
      total,
      sent: byStatus['sent'] ?? 0,
      failed: byStatus['failed'] ?? 0,
      bounced: byStatus['bounced'] ?? 0,
      sent_24h: Number(recent?.sent ?? 0),
      failed_24h: Number(recent?.failed ?? 0),
      success_rate: successRate,
      last_sent_at: lastSent?.created_at ?? null
    }]
  },

  casl: {
    subject: 'MailOverview',
    permissions: {
      SUPPORT: { actions: ['read'] }
    }
  },

  cache: {
    ttl: 30,
    invalidateOn: ['db.mail_log.created']
  }
}
